import { useCallback, useEffect, useState } from "react"
import { Plus, RefreshCw, Send, X } from "lucide-react"
import {
  fetchMemberMessageStatus,
  previewMemberMessages,
  sendMemberMessages,
  type MemberMessageBatch,
  type MemberMessageBlock,
  type MemberMessageOutcome,
  type MemberMessagePreview,
} from "@/lib/api"
import { UserCombobox, type UserOption } from "@/components/user-combobox"
import { Textarea } from "@/components/ui/textarea"
import { useReadOnly } from "@/lib/read-only"
import { cn, nameOrPhone } from "@/lib/utils"

const DRAFT_KEY = "offseason-admin-member-message-draft"
const MAX_MESSAGES = 5
const POLL_MS = 3000
const MAX_POLLS = 40

interface Draft {
  batchId: string
  recipients: UserOption[]
  messages: string[]
  sent: boolean
  sentIds: string[]
  skipped: Record<string, MemberMessageBlock>
}

const BLOCKED_LABEL: Record<MemberMessageBlock, string> = {
  no_profile: "No profile. Skipped.",
  no_first_name: "No first name on file and the message uses [fname]. Skipped.",
  no_imessage: "Has never texted us on iMessage. Skipped.",
  lookup_failed: "Couldn't check their iMessage chat. Preview again.",
}

const OUTCOME_LABEL: Record<MemberMessageOutcome | MemberMessageBlock, string> = {
  ...BLOCKED_LABEL,
  pending: "Sending. If this stays for more than a minute, Retry send is safe: nobody gets a text twice.",
  sent: "Sent on iMessage and copied to their coach chat.",
  uncertain: "Linq didn't confirm delivery. The relay rechecks it and never sends it twice.",
  stopped: "Not sent. They texted STOP or paused texts from us.",
  held: "Held. We texted them in the last hour or they reached their daily limit. Send it later as a new message.",
  not_delivered: "Not delivered.",
  failed: "Not sent. Linq had an error. Retry send is safe, nobody gets a text twice.",
  waiting: "Waiting for their quiet hours to end or for the relay to retry.",
  unknown: "Unknown. Check the backend log.",
}

function newDraft(): Draft {
  return { batchId: crypto.randomUUID(), recipients: [], messages: [""], sent: false, sentIds: [], skipped: {} }
}

function loadDraft(): Draft {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY)
    return raw ? { ...newDraft(), ...JSON.parse(raw) } : newDraft()
  } catch {
    return newDraft()
  }
}

function saveDraft(draft: Draft) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch {
    return
  }
}

function recipientLabel(r: UserOption): string {
  return nameOrPhone(`${r.first_name ?? ""} ${r.last_name ?? ""}`.trim()) || r.id.slice(0, 8)
}

function toBatch(draft: Draft, userIds?: string[]): MemberMessageBatch {
  return { user_ids: userIds ?? draft.recipients.map((r) => r.id), messages: draft.messages, batch_id: draft.batchId }
}

const UNFINISHED: ReadonlySet<MemberMessageOutcome> = new Set(["pending", "waiting", "uncertain"])

export function MemberMessagesPanel() {
  const { readOnly } = useReadOnly()
  const [draft, setDraft] = useState<Draft>(loadDraft)
  const [preview, setPreview] = useState<MemberMessagePreview[] | null>(null)
  const [previewedFor, setPreviewedFor] = useState<string | null>(null)
  const [outcomes, setOutcomes] = useState<Record<string, MemberMessageOutcome>>({})
  const [polls, setPolls] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => saveDraft(draft), [draft])

  const inputsKey = JSON.stringify([draft.recipients.map((r) => r.id), draft.messages])
  const previewIsCurrent = previewedFor === inputsKey
  const sendable = (preview ?? []).filter((r) => !r.blocked).length
  const canPreview = !draft.sent && draft.recipients.length > 0 && draft.messages.every((m) => m.trim()) && !busy
  const canSend = !readOnly && !draft.sent && previewIsCurrent && sendable > 0 && !busy

  const update = (patch: Partial<Draft>) => setDraft((prev) => ({ ...prev, ...patch }))

  const refreshStatus = useCallback(async () => {
    try {
      const status = await fetchMemberMessageStatus(toBatch(draft, draft.sentIds))
      setOutcomes(Object.fromEntries(status.results.map((r) => [r.user_id, r.outcome])))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't read the send status")
    }
  }, [draft])

  const pending = Object.values(outcomes).includes("pending")
  const unfinished = Object.values(outcomes).some((o) => UNFINISHED.has(o))
  const retryable = pending || Object.values(outcomes).includes("failed")

  useEffect(() => {
    if (!draft.sent) return
    if (Object.keys(outcomes).length === 0) {
      void refreshStatus()
      return
    }
    if (!unfinished || polls >= MAX_POLLS) return
    const timer = setTimeout(() => {
      setPolls((n) => n + 1)
      void refreshStatus()
    }, POLL_MS)
    return () => clearTimeout(timer)
  }, [draft.sent, outcomes, unfinished, polls, refreshStatus])

  async function runPreview() {
    setBusy(true)
    setError(null)
    try {
      setPreview(await previewMemberMessages(toBatch(draft)))
      setPreviewedFor(inputsKey)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Preview failed")
    } finally {
      setBusy(false)
    }
  }

  async function startSend(frozen: Draft) {
    if (readOnly || frozen.sentIds.length === 0) return
    setBusy(true)
    setError(null)
    try {
      await sendMemberMessages(toBatch(frozen, frozen.sentIds))
      setDraft({ ...frozen, sent: true })
      setOutcomes({})
      setPolls(0)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Send failed")
    } finally {
      setBusy(false)
    }
  }

  function confirmSend() {
    const n = draft.messages.length
    const ok = window.confirm(
      `Text ${sendable} member${sendable === 1 ? "" : "s"} ${n} message${n === 1 ? "" : "s"} each over iMessage? This can't be unsent.`,
    )
    if (!ok || !preview) return
    const approved = preview.filter((r) => !r.blocked)
    const skipped = Object.fromEntries(preview.flatMap((r) => (r.blocked ? [[r.user_id, r.blocked]] : [])))
    void startSend({ ...draft, sentIds: approved.map((r) => r.user_id), skipped })
  }

  function startOver() {
    if (busy) return
    if (draft.sent && unfinished && !window.confirm("This batch is still sending. Start a new message anyway?")) return
    setDraft(newDraft())
    setPreview(null)
    setPreviewedFor(null)
    setOutcomes({})
    setPolls(0)
    setError(null)
  }

  const canRetry = draft.sent && !readOnly && !busy && retryable
  const locked = draft.sent || busy

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-card p-4 space-y-4">
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">Recipients</div>
          <div className="flex flex-wrap items-center gap-2">
            {draft.recipients.map((r) => (
              <span key={r.id} className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-sm">
                {recipientLabel(r)}
                {!locked && (
                  <button
                    aria-label="Remove recipient"
                    onClick={() => update({ recipients: draft.recipients.filter((p) => p.id !== r.id) })}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </span>
            ))}
            {!locked && (
              <UserCombobox
                value={null}
                onSelect={(u) => {
                  if (u && !draft.recipients.some((p) => p.id === u.id)) update({ recipients: [...draft.recipients, u] })
                }}
                placeholder="Add a member"
              />
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">
            Messages. Each one is its own text, sent in order. [fname] becomes their first name.
          </div>
          {draft.messages.map((m, i) => (
            <div key={i} className="flex items-start gap-2">
              <Textarea
                value={m}
                disabled={locked}
                onChange={(e) => update({ messages: draft.messages.map((p, j) => (j === i ? e.target.value : p)) })}
                placeholder="Hey [fname], ..."
                rows={3}
              />
              {draft.messages.length > 1 && !locked && (
                <button
                  aria-label="Remove message"
                  onClick={() => update({ messages: draft.messages.filter((_, j) => j !== i) })}
                  className="mt-2 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
          ))}
          {draft.messages.length < MAX_MESSAGES && !locked && (
            <button
              onClick={() => update({ messages: [...draft.messages, ""] })}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <Plus className="size-4" /> Add another message
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!draft.sent && (
            <>
              <button
                onClick={() => void runPreview()}
                disabled={!canPreview}
                className="rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
              >
                Preview
              </button>
              <button
                onClick={confirmSend}
                disabled={!canSend}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                <Send className="size-4" />
                Send to {sendable} member{sendable === 1 ? "" : "s"}
              </button>
            </>
          )}
          {draft.sent && (
            <>
              <button
                onClick={() => void refreshStatus()}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent"
              >
                <RefreshCw className="size-4" /> Refresh status
              </button>
              {canRetry && (
                <button
                  onClick={() => void startSend(draft)}
                  className="rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent"
                >
                  Retry send
                </button>
              )}
            </>
          )}
          <button onClick={startOver} disabled={busy} className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50">
            New message
          </button>
          {readOnly && !draft.sent && (
            <span className="text-xs text-muted-foreground">Read-only mode is on. Turn it off to send.</span>
          )}
          {!draft.sent && preview && !previewIsCurrent && (
            <span className="text-xs text-muted-foreground">Changed since the preview. Preview again to send.</span>
          )}
          {busy && <span className="text-xs text-muted-foreground">Working...</span>}
        </div>
        {error && <div className="text-sm text-destructive">{error}</div>}
      </div>

      {draft.sent && <SentList draft={draft} preview={preview} outcomes={outcomes} unfinished={unfinished} />}
      {!draft.sent && preview && <PreviewList preview={preview} />}
    </div>
  )
}

function PreviewList({ preview }: { preview: MemberMessagePreview[] }) {
  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">Preview. Nothing sent yet.</div>
      {preview.map((r) => (
        <div key={r.user_id} className="rounded-lg border bg-card p-4 space-y-2">
          <div className="font-medium">{nameOrPhone(r.name) || r.user_id.slice(0, 8)}</div>
          {r.blocked ? (
            <div className="text-sm text-destructive">{BLOCKED_LABEL[r.blocked]}</div>
          ) : (
            r.bubbles.map((text, i) => (
              <div key={i} className="rounded-lg bg-muted px-3 py-2 text-sm whitespace-pre-wrap">{text}</div>
            ))
          )}
        </div>
      ))}
    </div>
  )
}

function outcomeFor(
  draft: Draft,
  outcomes: Record<string, MemberMessageOutcome>,
  userId: string,
): MemberMessageOutcome | MemberMessageBlock | undefined {
  return userId in draft.skipped ? draft.skipped[userId] : outcomes[userId]
}

function SentList({ draft, preview, outcomes, unfinished }: {
  draft: Draft
  preview: MemberMessagePreview[] | null
  outcomes: Record<string, MemberMessageOutcome>
  unfinished: boolean
}) {
  const bubbles = Object.fromEntries((preview ?? []).map((r) => [r.user_id, r.bubbles]))
  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">{unfinished ? "Sending..." : "Sent batch"}</div>
      {draft.recipients.map((r) => {
        const outcome = outcomeFor(draft, outcomes, r.id)
        return (
          <div key={r.id} className="rounded-lg border bg-card p-4 space-y-2">
            <div className="font-medium">{recipientLabel(r)}</div>
            {(bubbles[r.id] ?? []).map((text, i) => (
              <div key={i} className="rounded-lg bg-muted px-3 py-2 text-sm whitespace-pre-wrap">{text}</div>
            ))}
            <div className={cn("text-xs", outcome === "sent" ? "text-muted-foreground" : "text-destructive")}>
              {outcome ? OUTCOME_LABEL[outcome] : "Checking..."}
            </div>
          </div>
        )
      })}
    </div>
  )
}
